import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useRecoilState } from 'recoil';
import { AccessRefusedError, BackendClient } from '~/backend/backend-client';
import { CompositeCbctClient } from '~/backend/composite-cbct-client';
import { ContouringClient } from '~/backend/contouring-client';
import { DataClient } from '~/backend/data-client';
import { DoseClient } from '~/backend/dose-client';
import { MetricsClient } from '~/backend/metrics-client';
import { RegistrationClient } from '~/backend/registration-client';
import { TranslationClient } from '~/backend/translation-client';
import { APIType } from '~/backend/types';
import {
    TokenProvider,
    useAccessTokenProvider,
} from '~/hooks/auth/useAccessTokenProvider';
import { useAuthSession } from '~/contexts/auth/AuthSessionContext';
import { isAuthenticationEnabled } from '~/lib/auth';
import { generateSessionId, sessionIDState } from '~/state/atoms/backendAtoms';
import AppConfig from '~/utils/appconfig';
import { transformAvailableModelsData } from '~/utils/transforms/predictionModelsDataTransforms';

/**
 * Base URL for the backend REST clients. In connector mode there is no configured backend, so these calls target
 * the connector's own `/api` surface — which mirrors the backend's metadata routes (models, config, labeling) and
 * forwards them upstream — rather than the origin root. In standalone mode they go straight to the configured
 * backend. Only the base differs between deployments; the per-call request suffix is identical, so a single client
 * serves both.
 */
const backendClientBaseUrl = (): string =>
    AppConfig.isConnectorMode ? '/api' : AppConfig.backendUrl;

/**
 * A React hook to retrieve the current session ID
 *
 * @param {api} Model type to retrieve the models for
 *
 * @returns {BackendClient} Backend client
 *
 */
export const useSessionID = () => {
    const [sessionID, setSessionID] = useRecoilState(sessionIDState);

    const resetSessionID = () => {
        const newSessionID =
            import.meta.env.VITE_STATIC_SESSION_ID ?? generateSessionId();
        setSessionID(newSessionID);

        return newSessionID;
    };

    return {
        sessionID,
        resetSessionID,
    };
};

/**
 * A React hook to retrieve the backend connector by the model type
 *
 * @param {api} Model type to retrieve the models for
 *
 * @returns {BackendClient} Backend client
 *
 */
export const useBackend = (api: APIType): BackendClient => {
    const { sessionID } = useSessionID();
    const { tokenProvider } = useAccessTokenProvider({
        scopes: [`${AppConfig.clientId}/.default`],
    });

    switch (api) {
        case APIType.WORKSPACE:
            return new BackendClient(
                backendClientBaseUrl(),
                tokenProvider,
                sessionID
            );
        case APIType.CONTOURING:
            return new ContouringClient(
                backendClientBaseUrl(),
                tokenProvider,
                sessionID
            );
        case APIType.DOSE:
            return new DoseClient(
                backendClientBaseUrl(),
                tokenProvider,
                sessionID
            );
        case APIType.REGISTRATION:
            return new RegistrationClient(
                backendClientBaseUrl(),
                tokenProvider,
                sessionID
            );
        case APIType.TRANSLATION:
            return new TranslationClient(
                backendClientBaseUrl(),
                tokenProvider,
                sessionID
            );
        case APIType.DATA:
            return new ContouringClient(
                backendClientBaseUrl(),
                tokenProvider,
                sessionID
            );
        case APIType.METRICS:
            return new MetricsClient(
                backendClientBaseUrl(),
                tokenProvider,
                sessionID
            );
    }
    throw new Error('Unknown API type!');
};

/**
 * A React hook to retrieve the contouring backend connector
 *
 * @returns {ContouringClient} Backend client
 *
 */
export const useContouringBackend = () => {
    const { sessionID } = useSessionID();
    const { tokenProvider } = useAccessTokenProvider({
        scopes: [`${AppConfig.clientId}/.default`],
    });
    // useMemo to instantiate the client only when sessionID changes
    const client = useMemo(() => {
        return new ContouringClient(
            backendClientBaseUrl(),
            tokenProvider,
            sessionID
        );
    }, [sessionID, tokenProvider]);

    return client;
};

/**
 * A React hook to retrieve the dose backend connector
 *
 * @returns {DoseClient} Backend client
 *
 */
export const useDoseBackend = () => {
    const { sessionID } = useSessionID();
    const { tokenProvider } = useAccessTokenProvider({
        scopes: [`${AppConfig.clientId}/.default`],
    });

    const client = useMemo(() => {
        return new DoseClient(backendClientBaseUrl(), tokenProvider, sessionID);
    }, [sessionID, tokenProvider]);

    return client;
};

/**
 * A React hook to retrieve the data backend connector
 *
 * @returns {DoseClient} Backend client
 *
 */
export const useDataBackend = () => {
    const { sessionID } = useSessionID();
    const { tokenProvider } = useAccessTokenProvider({
        scopes: [`${AppConfig.clientId}/.default`],
    });

    const client = useMemo(() => {
        return new DataClient(backendClientBaseUrl(), tokenProvider, sessionID);
    }, [sessionID, tokenProvider]);

    return client;
};

/**
 * A React hook to retrieve the translation backend connector
 *
 * @returns {TranslationClient} Backend client
 *
 */
export const useTranslationBackend = () => {
    const { sessionID } = useSessionID();
    const { tokenProvider } = useAccessTokenProvider({
        scopes: [`${AppConfig.clientId}/.default`],
    });

    const client = useMemo(() => {
        return new TranslationClient(
            backendClientBaseUrl(),
            tokenProvider,
            sessionID
        );
    }, [sessionID, tokenProvider]);

    return client;
};

/**
 * A React hook to retrieve the registration backend connector
 *
 * @returns {RegistrationClient} Backend client
 *
 */
export const useRegistrationBackend = () => {
    const { sessionID } = useSessionID();
    const { tokenProvider } = useAccessTokenProvider({
        scopes: [`${AppConfig.clientId}/.default`],
    });

    const client = useMemo(() => {
        return new RegistrationClient(
            backendClientBaseUrl(),
            tokenProvider,
            sessionID
        );
    }, [sessionID, tokenProvider]);

    return client;
};

/**
 * React hook returning the composite-CBCT v2 backend client. Shares
 * the MSAL token scope + session id with the registration client
 * because composite CBCT lives on the same adapt+ backend surface.
 *
 * @returns {CompositeCbctClient} Backend client
 */
export const useCompositeCbctBackend = () => {
    const { sessionID } = useSessionID();
    const { tokenProvider } = useAccessTokenProvider({
        scopes: [`${AppConfig.clientId}/.default`],
    });

    const client = useMemo(() => {
        return new CompositeCbctClient(
            backendClientBaseUrl(),
            tokenProvider,
            sessionID
        );
    }, [sessionID, tokenProvider]);

    return client;
};

/**
 * A React hook to retrieve the metrics backend connector
 *
 * @returns {MetricsClient} Backend client
 *
 */
export const useMetricsBackend = () => {
    const { sessionID } = useSessionID();
    const { tokenProvider } = useAccessTokenProvider({
        scopes: [`${AppConfig.clientId}/.default`],
    });

    const client = useMemo(() => {
        return new MetricsClient(
            backendClientBaseUrl(),
            tokenProvider,
            sessionID
        );
    }, [sessionID, tokenProvider]);

    return client;
};

/**
 * A React hook to retrieve the user models from the backends. Requires the user to be signed in via AAD
 *
 * @param {api} Optional model type to retrieve the models for
 *
 * @returns {isPending} Boolean to show that the loading is in progress
 * @returns {string[] | undefined} errors (if any)
 * @returns {UserModels | undefined} Associated user models
 *
 */
/**
 * Optional React Query knobs the model/customization hooks forward. Kept intentionally small — only the options
 * the connector-mode workflows need (enable-gating for prefetch, polling interval).
 */
type BackendQueryOptions = {
    enabled?: boolean;
    refetchInterval?: number;
};

export const useBackendModels = (
    api: APIType = APIType.CONTOURING,
    options: BackendQueryOptions = {}
) => {
    const client = useBackend(api);

    return useQuery({
        queryKey: ['models', api],
        queryFn: async () => {
            const models = await client.models();
            return transformAvailableModelsData(models);
        },
        ...options,
    });
};

/**
 * A React hook to retrieve the user customizations from the backends. Requires the user to be signed in via AAD
 *
 * @param {api} Optional model type to retrieve the models for
 *
 * @returns {isPending} Boolean to show that the loading is in progress
 * @returns {string[] | undefined} errors (if any)
 * @returns {UserCustomization[] | undefined} Associated user models
 *
 */
export const useModelCustomizations = (
    api: APIType = APIType.CONTOURING,
    options: BackendQueryOptions = {}
) => {
    const client = useBackend(api);
    const { data: userId } = useBackendIdentity();
    const { enabled = true, ...queryOptions } = options;
    // The connector substitutes its own site identity on the way upstream, and
    // with no identity provider configured there is no user at all: in both
    // cases the client sends none and has nothing to wait for.
    const hasIdentity =
        AppConfig.isConnectorMode ||
        !isAuthenticationEnabled() ||
        Boolean(userId);

    return useQuery({
        ...queryOptions,
        // Waits for the identity: requesting customizations for the wrong user
        // is refused rather than answered, so there is nothing to show meanwhile.
        // Combined with the caller's gate, never replaced by it.
        enabled: enabled && hasIdentity,
        queryKey: ['customizations', api, userId],
        queryFn: async () => {
            return client.modelCustomizations(userId);
        },
    });
};

/**
 * The user id the backend resolves this session to, which is the id ownership
 * checks are made against.
 *
 * Asked rather than derived: the mapping from a broker subject to the internal
 * user id exists only on the backend, so a client that computes an id from token
 * claims disagrees with it for every identity that is not already internal.
 */
export const useBackendIdentity = () => {
    const client = useBackend(APIType.WORKSPACE);
    const { isAuthenticated } = useAuthSession();

    return useQuery({
        // The connector resolves identity itself and does not serve user/me.
        enabled: isAuthenticated && !AppConfig.isConnectorMode,
        queryKey: ['backend-identity'],
        queryFn: async () => (await client.identity()).user_id,
        staleTime: Infinity,
    });
};

const generateNewBackendClient = (
    tokenProvider: TokenProvider,
    sessionID: string
) => {
    return new BackendClient(backendClientBaseUrl(), tokenProvider, sessionID);
};

/** React Query key of the access check's cached answer. */
export const ACCESS_RIGHTS_QUERY_KEY = ['accessRight'];

/** How many times an access check that went unanswered is asked again. */
const ACCESS_CHECK_RETRIES = 5;

/**
 * Whether a failed access check is asked again. A refusal is the backend's
 * answer about this account and does not change on asking, so it is reported
 * at once; only a check that went unanswered is retried.
 */
export const retryAccessCheck = (
    failureCount: number,
    error: unknown
): boolean =>
    !(error instanceof AccessRefusedError) &&
    failureCount < ACCESS_CHECK_RETRIES;

/**
 * A hook to retrieve accessRights from the backend
 *
 * @returns {isPending} Boolean to show that the loading is in progress
 * @returns {string[] | undefined} errors (if any)
 * @returns {string} Access rights
 */
export const useAccessRights = (enabled: boolean = true) => {
    const { sessionID } = useSessionID();
    const { tokenProvider } = useAccessTokenProvider({
        scopes: [`${AppConfig.clientId}/.default`],
    });

    const client = useMemo(
        () => generateNewBackendClient(tokenProvider, sessionID),
        [tokenProvider, sessionID]
    );

    return useQuery({
        queryKey: ACCESS_RIGHTS_QUERY_KEY,
        queryFn: async () => {
            return client.accessRights();
        },
        enabled,
        retry: retryAccessCheck,
        retryDelay: (attemptIndex) => {
            return 1000 * attemptIndex ** 2; // 1, 4, 9, 16, 25 seconds
        },
        staleTime: 1000 * 60 * 30, // keeps successful result for 30 minutes
    });
};

/**
 * A React hook to retrieve the labeling from the backend
 *
 * @returns {isPending} Boolean to show that the loading is in progress
 * @returns {string[] | undefined} errors (if any)
 * @returns {Labeling[] | undefined} Associated labeling
 *
 */
export const useLabelingBackend = (
    api: APIType = APIType.CONTOURING,
    enabled: boolean = true
) => {
    const client = useBackend(api);

    return useQuery({
        queryKey: ['labeling', api],
        enabled: enabled,
        queryFn: async () => {
            return client.labeling(api);
        },
    });
};
